/* ==========================================================================
   STOCKISTS MAP
   A custom "gold line art" map style (assets/stockists-map-style.json)
   rendered with MapLibre GL JS against OpenFreeMap's free vector tiles - no
   API key, no account, no usage gating (unlike CartoDB/Mapbox/Stadia, which
   all require a key even for low-volume use). Custom diamond markers, a
   list-view alternative, a region filter shared between both views, and
   optional "use my location" distance sorting.

   Note: MapLibre uses [lng, lat] order throughout, the opposite of the
   [lat, lng] convention this file used under Leaflet previously.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-stockists]');
  if (!root) return;
  if (typeof maplibregl === 'undefined') return;

  // Lives as a sibling of the section in the markup, not a descendant -
  // scoping this query to `root` previously meant it was never found, threw
  // inside the try/catch below, and silently produced zero stockists.
  var jsonEl = document.querySelector('[data-stockists-json]');
  var stockists = [];
  try { stockists = jsonEl ? (JSON.parse(jsonEl.textContent) || []) : []; } catch (err) { stockists = []; }
  stockists = stockists.filter(function (s) { return s && s.lat != null && s.lng != null && s.lat !== '' && s.lng !== ''; })
    .map(function (s) { return Object.assign({}, s, { lat: parseFloat(s.lat), lng: parseFloat(s.lng) }); });

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function popupHtml(s) {
    var parts = ['<p class="stockists__popup-name">' + escapeHtml(s.name) + '</p>'];
    var addr = [s.address, s.suburb].filter(Boolean).join(', ');
    if (addr) parts.push('<p class="stockists__popup-address">' + escapeHtml(addr) + '</p>');
    var links = [];
    if (s.phone) links.push('<a href="tel:' + escapeHtml(s.phone) + '">' + escapeHtml(s.phone) + '</a>');
    if (s.website) links.push('<a href="' + escapeHtml(s.website) + '" target="_blank" rel="noopener">Visit website</a>');
    links.push('<a href="https://www.google.com/maps/dir/?api=1&destination=' + s.lat + ',' + s.lng + '" target="_blank" rel="noopener">Get directions</a>');
    parts.push('<div class="stockists__popup-links">' + links.join('') + '</div>');
    return parts.join('');
  }

  function distanceKm(lat1, lon1, lat2, lon2) {
    var R = 6371;
    var dLat = (lat2 - lat1) * Math.PI / 180;
    var dLon = (lon2 - lon1) * Math.PI / 180;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  var iconTemplate = root.querySelector('[data-marker-icon-template]');

  function makeMarkerEl(kind) {
    var wrap = document.createElement('div');
    if (kind === 'you') {
      wrap.className = 'stockists__you-marker';
      var dot = document.createElement('span');
      dot.className = 'stockists__you-dot';
      wrap.appendChild(dot);
      return wrap;
    }
    wrap.className = 'stockists__marker-wrap';
    if (iconTemplate) {
      wrap.appendChild(iconTemplate.content.cloneNode(true));
    }
    return wrap;
  }

  /* ======================================================================
     MAP
     ====================================================================== */
  var mapEl = root.querySelector('[data-map]');
  var styleUrl = window.__stockistsStyleUrl;

  var map = new maplibregl.Map({
    container: mapEl,
    style: styleUrl,
    center: [173.5, -41.2],
    zoom: 5.4,
    attributionControl: { compact: true }
  });
  // Scroll-to-zoom left enabled (MapLibre's default) rather than disabled -
  // visitors can zoom with the mouse wheel/trackpad, not just the +/- buttons.
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

  // Markers/fitBounds must wait for the map's first full render pass - doing
  // this immediately after `new maplibregl.Map()` (before the style/canvas
  // transform has actually settled) produces positions that look roughly
  // right in shape but drift further from correct the further they are from
  // the map's initial reference point.
  var markers = [];
  map.on('load', function () {
    markers = stockists.map(function (s) {
      var el = makeMarkerEl('stockist');
      var marker = new maplibregl.Marker({ element: el })
        .setLngLat([s.lng, s.lat])
        .setPopup(new maplibregl.Popup({ offset: 16 }).setHTML(popupHtml(s)))
        .addTo(map);
      marker.stockist = s;
      marker.el = el;
      return marker;
    });

    if (markers.length) {
      var bounds = new maplibregl.LngLatBounds();
      markers.forEach(function (m) { bounds.extend(m.getLngLat()); });
      map.fitBounds(bounds, { padding: 60, maxZoom: 11, duration: 0 });
    }

    applyRegionFilter();
  });

  var youMarker = null;

  /* ======================================================================
     VIEW TOGGLE
     ====================================================================== */
  var viewBtns = root.querySelectorAll('[data-view-btn]');
  var viewPanels = root.querySelectorAll('[data-view-panel]');
  viewBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var target = btn.dataset.viewBtn;
      viewBtns.forEach(function (b) {
        var active = b === btn;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-selected', String(active));
      });
      viewPanels.forEach(function (panel) {
        panel.hidden = panel.dataset.viewPanel !== target;
      });
      if (target === 'map') {
        // MapLibre needs a nudge after being unhidden, since it can't
        // measure a display:none container correctly.
        setTimeout(function () { map.resize(); }, 50);
      }
    });
  });

  /* ======================================================================
     REGION FILTER
     ====================================================================== */
  var regionSelect = root.querySelector('[data-region-select]');
  var listItems = Array.prototype.slice.call(root.querySelectorAll('[data-stockist]'));
  var emptyMessage = root.querySelector('[data-empty-message]');

  var regions = [];
  stockists.forEach(function (s) { if (s.region && regions.indexOf(s.region) === -1) regions.push(s.region); });
  regions.sort().forEach(function (region) {
    var opt = document.createElement('option');
    opt.value = region; opt.textContent = region;
    regionSelect.appendChild(opt);
  });

  function applyRegionFilter() {
    var region = regionSelect.value;
    var visibleCount = 0;
    var matchedBounds = new maplibregl.LngLatBounds();
    markers.forEach(function (marker) {
      var match = !region || marker.stockist.region === region;
      marker.el.style.display = match ? '' : 'none';
      if (match) {
        visibleCount++;
        matchedBounds.extend(marker.getLngLat());
      }
    });
    listItems.forEach(function (item) {
      var match = !region || item.dataset.region === region;
      item.hidden = !match;
    });
    if (emptyMessage) emptyMessage.hidden = listItems.length > 0 ? visibleCount > 0 : false;

    // Zoom the map to whatever's now selected - the matching region, or back
    // out to everything when "All Regions" is chosen again.
    if (visibleCount > 0) {
      map.fitBounds(matchedBounds, { padding: 60, maxZoom: region ? 13 : 11, duration: 800 });
    }
  }
  regionSelect.addEventListener('change', applyRegionFilter);

  /* ======================================================================
     USE MY LOCATION
     ====================================================================== */
  var locateBtn = root.querySelector('[data-locate-btn]');
  if (locateBtn && 'geolocation' in navigator) {
    locateBtn.addEventListener('click', function () {
      locateBtn.classList.add('is-locating');
      navigator.geolocation.getCurrentPosition(function (pos) {
        locateBtn.classList.remove('is-locating');
        var lat = pos.coords.latitude, lng = pos.coords.longitude;

        if (youMarker) youMarker.remove();
        var youEl = makeMarkerEl('you');
        youMarker = new maplibregl.Marker({ element: youEl })
          .setLngLat([lng, lat])
          .setPopup(new maplibregl.Popup({ offset: 12 }).setHTML('You are here'))
          .addTo(map);
        youMarker.togglePopup();
        map.flyTo({ center: [lng, lat], zoom: 10, duration: 800 });

        markers.forEach(function (marker) {
          marker.stockist.distance = distanceKm(lat, lng, marker.stockist.lat, marker.stockist.lng);
        });
        var sorted = stockists.slice().sort(function (a, b) { return (a.distance || 0) - (b.distance || 0); });

        var list = root.querySelector('[data-list]');
        sorted.forEach(function (s) {
          var item = listItems.filter(function (el) { return el.dataset.lat == s.lat && el.dataset.lng == s.lng; })[0];
          if (!item) return;
          list.appendChild(item);
          var distEl = item.querySelector('[data-distance]');
          if (distEl) {
            distEl.textContent = s.distance.toFixed(1) + ' km away';
            distEl.hidden = false;
          }
        });
      }, function () {
        locateBtn.classList.remove('is-locating');
      }, {
        // High-accuracy asks the device for its best available fix (GPS on
        // mobile) instead of the fast/coarse network-based default, and
        // maximumAge:0 stops the browser handing back a stale cached fix.
        enableHighAccuracy: true, timeout: 10000, maximumAge: 0
      });
    });
  } else if (locateBtn) {
    locateBtn.hidden = true;
  }
})();
